import React from "react";
import {Button, IconButton} from "@mui/material";
import EntityList from "../../components/list/EntityList";
import buildUrl from "../../utils/api/buildUrl";
import useSWR from "swr";
import {deleteFetcher, getFetcher} from "../../utils/api/fetchers";
import {CategoryShort} from "../../schemas/category/categoriesSchema";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import SailingIcon from '@mui/icons-material/Sailing';
import {useNavigate} from "react-router-dom";
import {useNotification} from "../../utils/hooks/useNotification";
import {useConfirm} from "material-ui-confirm";
import {useAuth} from "../../utils/hooks/useAuth";
import UserRole from "../../schemas/user/roles";
import {getCategoryColor} from "../../utils/categories/categoryColors";

const Categories: React.FC = () => {
    const {hasPermission} = useAuth();
    const navigate = useNavigate();
    const {showNotification} = useNotification();
    const confirm = useConfirm();

    const url = buildUrl('/categories');
    const {data, mutate} = useSWR<CategoryShort[]>(url, getFetcher, {keepPreviousData: true});
    const editPermission = hasPermission(UserRole.COMMODORE);
    const deletePermission = hasPermission(UserRole.ADMIN);

    const handleCreateCategory = () => {
        navigate('/categories/create');
    }

    const handleDetails = (categoryId: number) => {
        navigate(`/categories/${categoryId}`);
    }

    const handleDelete = (categoryId: number) => {
        confirm({
            description: `Opravdu chcete smazat tuto kategorii?`,
            title: "Smazat kategorii",
            confirmationText: "Smazat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                deleteFetcher(`/categories/${categoryId}`).then(() => {
                    showNotification('Kategorie byla smazána', 'success');
                    mutate();
                }).catch((err) => {
                    if (err.response?.status === 409) {
                        showNotification('Kategorii nelze smazat, protože obsahuje lodě', 'error');
                        return;
                    }
                    showNotification('Kategorii se nepodařilo smazat', 'error');
                });
            })
            .catch(() => {
            });
    }

    const handleBoats = (categoryId: number) => {
        navigate(`/boats?default_filter:category=${data?.find(c => c.id === categoryId)?.categoryNumber}`);
    }

    return (
        <EntityList
            title="Kategoire"
            controls={editPermission ? [
                <Button
                    key="add"
                    variant="contained"
                    color="success"
                    onClick={handleCreateCategory}
                >
                    Vytvořit kategorii
                </Button>
            ] : []}
            headers={[
                {key: 'categoryNumber', label: 'Číslo kategorie'},
                {key: 'name', label: 'Název', render: (name, entity) => (
                    <span style={{color: getCategoryColor(entity.categoryNumber), fontWeight: 'bold'}}>
                        {name}
                    </span>
                )},
                {
                    key: 'controls', render: (category) => (
                        <div>
                            {!editPermission && (
                                <IconButton color="info" title="Detaily"
                                            onClick={() => handleDetails(category.id)}>
                                    <InfoIcon/>
                                </IconButton>
                            )}
                            <IconButton color="info" title="Lodě v kategorii"
                                        onClick={() => handleBoats(category.id)}>
                                <SailingIcon/>
                            </IconButton>
                            {editPermission && (
                                    <IconButton color="info" title="Detaily a úprava"
                                                onClick={() => handleDetails(category.id)}>
                                        <EditIcon/>
                                    </IconButton>
                            )}
                            {deletePermission && (
                                    <IconButton color="error" title="Smazat"
                                                onClick={() => handleDelete(category.id)}>
                                        <DeleteIcon/>
                                    </IconButton>
                            )}
                        </div>
                    )
                }
            ]}
            entities={data ?? []}
            entitiesCount={data?.length ?? 0}
            paged={false}
        />
    );
}

export default Categories;